import queryString from 'query-string';
import get from 'lodash/get';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_BOOKSHELF_DETAIL_SET_DATA,
  CATALOG_BOOKSHELF_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { ssr, bookshelfDetail: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);
    const subscriptionId = get(ssr, 'query.s', null);
    const bookType = get(ssr, 'query.t', null);

    const { userId, bookshelfId } = match.params;

    let newState = {
      bookshelfId,
      userId,
      pageNumber,
      subscriptionId,
      bookType,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.bookshelf) {
      newState = {
        ...newState,
        ...currentState,
      };
    }
    dispatch(setData(newState));

    let promises = [];
    // only fetch bookshelf detail if we dont have it already in the initial state
    if (!newState.bookshelf || newState.bookshelf.description === undefined) {
      promises.push(dispatch(fetchDetail(match)));
    }
    // same for the books
    if (!newState.books || currentState.pageNumber !== pageNumber) {
      promises.push(dispatch(fetchBookList(match)));
    }
    // return a promise that can be awaited server side
    await (promises.length > 0 ? Promise.all(promises) : Promise.resolve(null));
  };
}

export function fetchDetail(match) {
  return async(dispatch) => {
    const { bookshelfId } = match.params;
    const url = `/api/bookshelf/${bookshelfId}/`;

    return (
      fetchQuery(url, { apiVersion: 3 })
        .then(({ data }) => {
          dispatch(setData({ bookshelf: data }));
        })
        .catch(({ data, resp, error }) => {
          if (resp.status === 404) {
            dispatch(Error404Actions.show());
          }
          else {
            Raven.captureMessage('failed to load bookshelf detail due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load bookshelf detail due to server error');
          }
        })
    );
  };
}

export function fetchBookList(params={}) {
  return async(dispatch, getState) => {
    const { bookshelfDetail } = getState();
    const mergedState = { ...bookshelfDetail, ...params };
    const { paginateBy, bookshelfId, subscriptionId, bookType } = mergedState;

    if (!bookshelfDetail) {
      return null;
    }

    let pageNumber = mergedState.pageNumber || 1;
    if (!params.pageNumber &&
      (bookshelfDetail.subscriptionId !== mergedState.subscriptionId)
      ||(bookshelfDetail.bookType !== mergedState.bookType)
    ) {
      pageNumber = 1;
    }

    const queryParams = {
      limit: paginateBy,
      ...(subscriptionId!==null && { subscription_id__in: subscriptionId }),
      ...(pageNumber > 1 && { offset: (pageNumber - 1) * paginateBy }),
      ...(bookType!==null && { type: bookType }),
    };

    const qs = queryString.stringify(queryParams);
    const url = `/api/bookshelf/${bookshelfId}/books/?${qs}`;
    // clear the book list before the fetch
    dispatch(setData({
      books: null,
      // optimistically update the UI
      subscriptionId: null,
      bookType: null,
      pageNumber: pageNumber,
    }));
    return (
      fetchQuery(url, { apiVersion: 5 })
        .then(
          ({ data }) => {
            const lastPageNumber = Math.ceil(data.meta.total_count / paginateBy);
            dispatch(setData({
              books: data.objects,
              lastPageNumber: lastPageNumber,
              subscriptionId,
              bookType,
            }));
          }
        )
        .catch(
          ({ data, resp, error }) => {
            Raven.captureMessage('failed to load bookshelf books due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            if (resp.status !== 404) {
              throw new Error('failed to load bookshelf books due to server error');
            }
          }
        )
    );
  };
}

export const deleteBookshelf = (bookshelfId) => {
  return async() => {
    const url = `/api/bookshelf/${bookshelfId}/`;
    const params = {
      apiVersion: 5,
      method: 'DELETE',
    };
    return (
      fetchQuery(url, params)
        .then(() => {
          return true;
        })
        .catch(({ data, resp, error }) => {
          Raven.captureMessage('failed to delete bookshelf due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          return false;
        })
    );
  };
};

export const deleteBookFromBookshelf = ({ bookshelfId, bookId }) => {
  return async() => {
    const url = `/api/bookshelf/${bookshelfId}/bookusershelf/?book=${bookId}`;
    const params = {
      apiVersion: 5,
      method: 'DELETE',
    };
    return (
      fetchQuery(url, params)
        .then(() => {
          return true;
        })
        .catch(({ data, resp, error }) => {
          Raven.captureMessage('failed to delete bookshelf due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          return false;
        })
    );
  };
};

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { userId, bookshelf } = getState().bookshelfDetail;
    const title = bookshelf && bookshelf.name;
    const canonicalUrl = `https://${BASE_HOST}/profile/${userId}/`;
    bookshelf && (
      dispatch(SeoActions.setData({
        canonicalUrl,
        title,
      }))
    );
  };
}

export function setData(payload) {
  return (dispatch, getState) => {
    const { userId, bookshelf } = getState().bookshelfDetail;
    dispatch({
      type: CATALOG_BOOKSHELF_DETAIL_SET_DATA,
      payload,
    });
    if (userId, bookshelf) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_BOOKSHELF_DETAIL_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
